import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ActionsStatus from "../Reusable/ActionsStatus/ActionsStatus.tsx";
import { DownloadpageActionComponent } from "./DownloadpageActionComponent.js";
import FilesPreviewComponent from "./FilesPreviewComponent.js";
import { IconWithTooltip } from "components/Elements/Icon/Icon.tsx";
import InboxItemSelectComponent from "../Reusable/InboxItemSelectComponent.js";
import InboxOutboxLabel from "./InboxOutboxLabel.tsx";
import LabelTagList from "../Reusable/LabelTagList.tsx";
import MailActionsComponent from "../Reusable/MailActionsComponent.js";
import MailDataComponent from "../Reusable/MailDataComponent.js";
import MailType from "../Reusable/MailType.tsx";
import SenderInfo from "./SenderInfo/SenderInfo.tsx";
import StatusElement from "./StatusElement.tsx";
import Tooltip from "../../../Elements/Tooltip/Tooltip.js";
import config from "config";
import useAvoidFirstMount from "hooks/useMountedState.js";
import { decode } from "html-entities";
import { useContent } from "../../../../contexts/contentContext/contentContext.js";
import useInbox from "hooks/useInbox.ts";
import { useSaveInboxMail } from "../../../../hooks/useSaveInboxMail.js";
import { showSaveAction, showDeleteAction } from "../helpers.js";
import JFormTitle from "./components/JFormTitle/index.tsx";
import useRemoveMailFunction from "hooks/useRemoveMailFunction.js";
import LoadingImageElement from "components/Elements/LoadingContainerElement/LoadingImageElement.tsx";
import JFormInfo from "./components/JFormInfo/index.tsx";
import { useAppSelector } from "store/hooks.ts";
import { selectTParam, selectFParam } from "store/selectors/inboxSelectors.ts";
import { DOMAIN_NAME } from "utils/globalConsts.ts";

export const ItemBoderElem = ({ isSelected }) => (
  <div className={`inbox-item-border${isSelected ? " selected" : ""}`} />
);

const MAX_FULL_VIEW_WIDTH = 1000;
const LOADER_ICON_PATH =
  "https://resources.jumbomail.me/assets/images/preloader.gif";

const InboxListMail = ({ data, index, all, trash }) => {
  const [filesWasAdded, setFilesWasAdded] = useState(false);
  const { getTextByKey, getPath, getDirection } = useContent();
  const {
    getSplitView,
    setSplitView,
    setSelectedMail,
    setHoveredMail,
    getSelectedMail,
  } = useInbox();
  const pageType = useAppSelector(selectTParam);
  const pageCategory = useAppSelector(selectFParam);
  const listItemRef = useRef(null);
  const [isCompactView, setIsCompactView] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { handleClick: handleSave, isSaved } = useSaveInboxMail({
    mailData: data,
  });
  const handleDeleteMail = useRemoveMailFunction({
    activeArray: [data.id],
    setLoading: () => {},
    filesSize: data.filesSize,
    setLoading: setIsDeleting,
  });

  const handleResize = useCallback(() => {
    if (listItemRef?.current) {
      setIsCompactView(listItemRef?.current?.clientWidth < MAX_FULL_VIEW_WIDTH);
    }
  }, [listItemRef?.current]);

  useEffect(() => {
    if (listItemRef?.current) {
      // calculate the width after entrance animation finished
      setTimeout(() => {
        setIsCompactView(
          listItemRef?.current?.clientWidth < MAX_FULL_VIEW_WIDTH
        );
      }, 300);
    }
  }, [listItemRef?.current, getSplitView()]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [listItemRef?.current]);

  const handleSelectMail = (e) => {
    if (!getSplitView()) {
      setSplitView(true);
    }
    setSelectedMail(data);
  };

  // detecting when files were added to mail and highlight it
  const handleChangeFilesCount = () => {
    setFilesWasAdded(true);
    setTimeout(() => {
      setFilesWasAdded(false);
    }, 3000);
  };

  useAvoidFirstMount({
    callback: handleChangeFilesCount,
    dependencies: [data.filesQuantity],
  });


  const baseUrl = data?.bucketName
    ? data.bucketName.includes("jumbomail1")
      ? window.location.hostname.includes("test")
        ? config.baseurl
        : `https://${DOMAIN_NAME}`
      : data.bucketName.replace("files.", "")
    : config.baseurl;


  return (
    <div
      id={"inbox-list-mail-" + data?.id}
      className="inbox-list-mail"
      onClick={handleSelectMail}
      ref={listItemRef}
      data-id={data?.id}
      dir={getDirection()}
      onMouseEnter={() => setHoveredMail(data)}
      onMouseLeave={() => setHoveredMail(null)}
      style={{
        height: "auto",
      }}
    >
      <InboxItemSelectComponent id={data.id} />
      <div
        className={`inbox-list-mail-container${
          getSelectedMail()?.id === data?.id
            ? " inbox-list-mail-container-selected"
            : ""
        }${filesWasAdded ? " highlighted" : ""}`}
        style={{ animationDelay: `${index / 10}s` }}
      >
        <ItemBoderElem isSelected={getSelectedMail()?.id === data?.id} />
        <div className="inbox-list-jform-top">
          <div className="mail-subject-wrapper">
            <MailType {...data} />
            {data?.isJForm ? (
              <JFormTitle
                deliveryNumber={data?.deliveryNumber}
                formSubject={data?.formSubject}
                id={data?.id}
                bucketName={data?.bucketName}
              />
            ) : (
              <a
                className="mail-subject"
                href={`${baseUrl}${getPath()}gallery/${data.id}`}
                rel="noopener noreferrer"
                target="_blank"
              >
                {data.subject?.length > 0 ? (
                  <Tooltip tooltipText={decode(data.subject)}>
                    {decode(data.subject)}
                  </Tooltip>
                ) : (
                  getTextByKey("no-subject-text")
                )}
              </a>
            )}
          </div>
          <div className="mail-type-and-tags">
            {(all || pageType === "trash") && (
              <InboxOutboxLabel
                isOutbox={data?.isOutbox}
                isJForm={data?.isJForm}
              />
            )}
            {data?.labelIds ? (
              <LabelTagList
                labelIds={data?.labelIds}
                encryptedMailId={data.id}
              />
            ) : (
              <div />
            )}
          </div>
          <div className="mail-statuses-wrapper">
            {!trash ? (
              <div className="mail-action-statuses">
                {data?.mailActionStatus?.map((status) => (
                  <ActionsStatus status={status} key={status} />
                ))}
              </div>
            ) : null}
            <StatusElement data={data} />
          </div>
          <div className="jform-actions">
            {showDeleteAction(data, pageType) ? (
              <div className="save-remove-icon-container">
                {isDeleting ? (
                  <LoadingImageElement />
                ) : (
                  <>
                    {data.isOutbox || (data?.isJForm && data?.isSaved) ? (
                      <IconWithTooltip
                        iconProps={{
                          name: "trash-solid",
                          onClick: handleDeleteMail,
                          medium: true,
                        }}
                        tooltipProps={{
                          tooltipTextKey: "tooltip-action-delete-mail-text",
                        }}
                      />
                    ) : (
                      <IconWithTooltip
                        iconProps={{
                          name: isSaving
                            ? null
                            : (
                                pageCategory === "jform"
                                  ? isSaved
                                  : data.mailStatus === "saved"
                              )
                            ? "unsave-solid"
                            : "save-solid",
                          onClick: async () => {
                            setIsSaving(true);
                            await handleSave();
                            setIsSaving(false);
                          },
                          src: isSaving ? LOADER_ICON_PATH : null,
                          medium: true,
                        }}
                        tooltipProps={{
                          tooltipTextKey: `tooltip-action-${
                            (
                              pageCategory === "jform"
                                ? isSaved
                                : data.mailStatus === "saved"
                            )
                              ? "unsave-mail"
                              : "save-mail"
                          }-text`,
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            ) : null}
            {showSaveAction(data, pageType) ? (
              <div className="save-remove-icon-container">
                <IconWithTooltip
                  iconProps={{
                    name: isSaving
                      ? null
                      : (
                          pageCategory === "jform"
                            ? isSaved
                            : data.mailStatus === "saved"
                        )
                      ? "unsave-solid"
                      : "save-solid",
                    onClick: async () => {
                      setIsSaving(true);
                      await handleSave();
                      setIsSaving(false);
                    },
                    src: isSaving ? LOADER_ICON_PATH : null,
                    medium: true,
                  }}
                  tooltipProps={{
                    tooltipTextKey: `tooltip-action-${
                      (
                        pageCategory === "jform"
                          ? isSaved
                          : data.mailStatus === "saved"
                      )
                        ? "unsave-mail"
                        : "save-mail"
                    }-text`,
                  }}
                />
              </div>
            ) : null}
            <DownloadpageActionComponent data={data} isJmForm={data?.isJForm} />
          </div>
          {document.getElementById("inbox-list-mail-" + data?.id) ? (
            createPortal(
              <MailActionsComponent
                mailData={data}
                listItemRef={listItemRef}
                all={all}
                isFirstElementForProductTour={!index}
              />,
              document.getElementById("inbox-list-mail-" + data?.id)
            )
          ) : (
            <MailActionsComponent
              mailData={data}
              listItemRef={listItemRef}
              all={all}
              isFirstElementForProductTour={!index}
            />
          )}
        </div>
        <div className="inbox-list-jform-bottom">
          {data?.isJForm ? (
            <div className="inbox-sender-info-wrapper">
              <JFormInfo
                senderEmail={data?.SenderInfo?.senderEmail}
                fullName={
                  (data?.SenderInfo?.fullName + "").trim() ||
                  data?.SenderInfo?.senderEmail
                }
                creationDate={data?.creationDate}
                formFields={data?.formFields}
                isOutbox={data?.isOutbox}
                profilePhotoUrl={data?.SenderInfo?.profilePhotoUrl}
                recipientsList={data?.recipientsList}
                recipientInfo={data?.RecipientInfo}
              />
            </div>
          ) : (
            <div className="inbox-sender-info-wrapper">
              {!!data?.SenderInfo && (
                <SenderInfo
                  {...data?.SenderInfo}
                  fullName={
                    (data?.SenderInfo?.fullName + "").trim() ||
                    data?.SenderInfo?.senderEmail
                  }
                  relatedTo={data?.relatedTo}
                  deletedUserFullName={data?.deletedUserFullName}
                  creationDate={data?.creationDate}
                  filesQuantity={data?.filesQuantity}
                  totalSize={data?.filesSize}
                  recipients={data?.recipientsList}
                  recipientInfo={data?.RecipientInfo}
                  isCompactView={isCompactView}
                  isOutbox={data?.isOutbox}
                />
              )}
            </div>
          )}
          {!getSplitView() && (
            <div className="files-preview-component-wrapper">
              <FilesPreviewComponent data={data} />
            </div>
          )}
          <MailDataComponent mailData={data} />
        </div>
      </div>
    </div>
  );
  // <div
  //   id={"inbox-list-mail-" + data?.id}
  //   className="inbox-list-mail"
  //   onClick={handleSelectMail}
  //   ref={listItemRef}
  //   data-id={data?.id}
  //   dir={getDirection()}
  //   onMouseEnter={() => setHoveredMail(data)}
  //   onMouseLeave={() => setHoveredMail(null)}
  // >
  //   <InboxItemSelectComponent id={data.id} />
  //   <div
  //     className={`inbox-list-mail-container${
  //       getSelectedMail()?.id === data?.id
  //         ? " inbox-list-mail-container-selected"
  //         : ""
  //     }${
  //       data?.downloadsQuantity && !data?.isJForm
  //         ? " downloaded"
  //         : " not-downloaded"
  //     }${filesWasAdded ? " highlighted" : ""}`}
  //     style={{ animationDelay: `${index / 10}s` }}
  //   >
  //     <ItemBoderElem isSelected={getSelectedMail()?.id === data?.id} />
  //     <div className="inbox-list-mail-top">
  //       <div className="mail-subject-wrapper">
  //         <MailType {...data} />
  //         <a
  //           className="mail-subject"
  //           href={`${config.baseurl}${getPath()}gallery/${data.id}`}
  //           rel="noopener noreferrer"
  //           target="_blank"
  //         >
  //           {data.subject?.length > 0 ? (
  //             <Tooltip tooltipText={decode(data.subject)}>
  //               {decode(data.subject)}
  //             </Tooltip>
  //           ) : (
  //             getTextByKey("no-subject-text")
  //           )}
  //         </a>
  //         <LabelTagList labelIds={data?.labelIds} encryptedMailId={data.id} />
  //         {(all || getSearchParams().t === "trash") && (
  //           <InboxOutboxLabel
  //             isOutbox={data?.isOutbox}
  //             isJForm={data?.isJForm}
  //           />
  //         )}
  //       </div>
  //       {!trash ? (
  //         <div className="mail-action-statuses">
  //           {data?.mailActionStatus?.map((status) => (
  //             <ActionsStatus status={status} key={status} />
  //           ))}
  //           {/*
  //              REMOVED DUE TO DOUBLE PENDING DOWNLOAD STATUS TAG,
  //              data?.mailActionStatus includes already PendingDownload status

  //              {data?.mailStatus === "active" &&
  //               data?.downloadsQuantity === 0 &&
  //               data?.isOutbox === false && (
  //                 <ActionsStatus status="PendingDownload" />
  //               )} */}
  //         </div>
  //       ) : null}
  //       <StatusElement data={data} />
  //       {/*
  //             gap for the actions component.
  //             it's using in lot's of places so I decided
  //             not to change MailActionsComponent and just override styles
  //           */}
  //       <div className="mail-actions-component-gap" />
  //       {document.getElementById("inbox-list-mail-" + data?.id) ? (
  //         createPortal(
  //           <MailActionsComponent
  //             mailData={data}
  //             listItemRef={listItemRef}
  //             all={all}
  //             isFirstElementForProductTour={!index}
  //           />,
  //           document.getElementById("inbox-list-mail-" + data?.id)
  //         )
  //       ) : (
  //         <MailActionsComponent
  //           mailData={data}
  //           listItemRef={listItemRef}
  //           all={all}
  //           isFirstElementForProductTour={!index}
  //         />
  //       )}
  //     </div>
  //     <div className="inbox-list-mail-bottom">
  //       <div className="inbox-sender-info-wrapper">
  //         {!!data?.SenderInfo && (
  //           <SenderInfo
  //             {...data?.SenderInfo}
  //             relatedTo={data?.relatedTo}
  //             deletedUserFullName={data?.deletedUserFullName}
  //             creationDate={data?.creationDate}
  //             filesQuantity={data?.filesQuantity}
  //             totalSize={data?.filesSize}
  //             recipients={data?.recipientsList}
  //             recipientInfo={data?.RecipientInfo}
  //             isCompactView={isCompactView}
  //             isOutbox={data?.isOutbox}
  //           />
  //         )}
  //       </div>
  //       {!getSplitView() && (
  //         <div className="files-preview-component-wrapper">
  //           <FilesPreviewComponent data={data} />
  //         </div>
  //       )}
  //       <div className="flex-spacer"></div>
  //       {showDeleteAction(data, getSearchParams) ? (
  //         <div className="save-remove-icon-container">
  //           {isDeleting ? (
  //             <LoadingImageElement />
  //           ) : (
  //             <>
  //               {data.isOutbox ? (
  //                 <IconWithTooltip
  //                   iconProps={{
  //                     name: "trash-solid",
  //                     onClick: handleDeleteMail,
  //                     medium: true,
  //                   }}
  //                   tooltipProps={{
  //                     tooltipTextKey: "tooltip-action-delete-mail-text",
  //                   }}
  //                 />
  //               ) : (
  //                 <IconWithTooltip
  //                   iconProps={{
  //                     name: isSaving
  //                       ? null
  //                       : (
  //                           getSearchParams().f === "jform"
  //                             ? isSaved
  //                             : data.mailStatus === "saved"
  //                         )
  //                       ? "unsave-solid"
  //                       : "save-solid",
  //                     onClick: async () => {
  //                       setIsSaving(true);
  //                       await handleSave();
  //                       setIsSaving(false);
  //                     },
  //                     src: isSaving ? loader : null,
  //                     medium: true,
  //                   }}
  //                   tooltipProps={{
  //                     tooltipTextKey: `tooltip-action-${
  //                       (
  //                         getSearchParams().f === "jform"
  //                           ? isSaved
  //                           : data.mailStatus === "saved"
  //                       )
  //                         ? "unsave-mail"
  //                         : "save-mail"
  //                     }-text`,
  //                   }}
  //                 />
  //               )}
  //             </>
  //           )}
  //         </div>
  //       ) : null}
  //       {showSaveAction(data, getSearchParams) ? (
  //         <div className="save-remove-icon-container">
  //           <IconWithTooltip
  //             iconProps={{
  //               name: isSaving
  //                 ? null
  //                 : (
  //                     getSearchParams().f === "jform"
  //                       ? isSaved
  //                       : data.mailStatus === "saved"
  //                   )
  //                 ? "unsave-solid"
  //                 : "save-solid",
  //               onClick: async () => {
  //                 setIsSaving(true);
  //                 await handleSave();
  //                 setIsSaving(false);
  //               },
  //               src: isSaving ? loader : null,
  //               medium: true,
  //             }}
  //             tooltipProps={{
  //               tooltipTextKey: `tooltip-action-${
  //                 (
  //                   getSearchParams().f === "jform"
  //                     ? isSaved
  //                     : data.mailStatus === "saved"
  //                 )
  //                   ? "unsave-mail"
  //                   : "save-mail"
  //               }-text`,
  //             }}
  //           />
  //         </div>
  //       ) : null}
  //       <DownloadpageActionComponent data={data} isJmForm={data?.isJForm} />
  //       <MailDataComponent mailData={data} />
  //     </div>
  //   </div>
  // </div>
};

export default InboxListMail;
